import React, { useState } from "react";
import { MenuItem, Typography, Tooltip, Box } from "@mui/material";
import { C, fmtINR } from "../constants";
import { IPD_COLORS } from "../../ipd/utils/ipdColors";
import { ipdLabelSx } from "../../ipd/utils/ipdFieldStyles";
import { useBillingPermissions } from "../useBillingPermissions";
import { BILLING_PERMS } from "../billing-api";
import { COUNTER_CODES } from "../useCashCounter";
import IpdSectionCard from "../../ipd/utils/IpdSectionCard";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import SvgIcon from "../SvgIcon";
import {
  StatTile, SessionChip, ErrorBar, ModeTotals, ModalShell, fmtDateTime,
} from "./CashCounterUI";

// ─────────────────────────────────────────────────────────────────────────────
// Screen 1 — Cash Counter Dashboard.
// Header (counter selector, status chip, Open Session, Refresh), summary cards,
// mode split, and the action row. Every action is permission-gated.
// ─────────────────────────────────────────────────────────────────────────────

function OpenSessionModal({ counterCode, busy, onClose, onSubmit }) {
  const [openingBalance, setOpeningBalance] = useState("0.00");
  const [remarks, setRemarks] = useState("");
  return (
    <ModalShell
      title="Open Counter Session"
      subtitle={`Start the shift window for ${counterCode}.`}
      onClose={onClose}
      footer={<>
        <IpdButton variant="outlined" onClick={onClose} disabled={busy} sx={{ flex: 1 }}>Cancel</IpdButton>
        <IpdButton
          tone="teal"
          onClick={() => onSubmit({ openingBalance, remarks: remarks.trim() || undefined })}
          disabled={busy}
          sx={{ flex: 2 }}
        >
          {busy ? "Opening…" : "Open Session"}
        </IpdButton>
      </>}
    >
      <IpdFormField label="Opening balance (₹)">
        <IpdTextField
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
          value={openingBalance}
          onChange={(e) => setOpeningBalance(e.target.value)}
          fullWidth
          sx={{ "& .MuiOutlinedInput-input": { fontWeight: 700 } }}
        />
      </IpdFormField>
      <IpdFormField label="Remarks">
        <IpdTextField
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Morning shift start"
          fullWidth
        />
      </IpdFormField>
    </ModalShell>
  );
}

export default function CashCounterDashboard({ cc, onViewInterim, onReviewFinal }) {
  const { can } = useBillingPermissions();
  const [modal, setModal] = useState(null); // "open" | "final"

  const canInterimView = can(BILLING_PERMS.SCROLL_INTERIM_VIEW);
  const canInterimPrint = can(BILLING_PERMS.SCROLL_INTERIM_PRINT);
  const canFinalView = can(BILLING_PERMS.SCROLL_FINAL_VIEW);
  const canFinalClose = can(BILLING_PERMS.SCROLL_FINAL_CLOSE);

  const { counterCode, setCounterCode, counters, lockedByOther, lockedByLabel, session, scroll, finalScrollId, busy, error, canRetry, retry } = cc;
  const isOpen = session?.status === "OPEN";
  const isClosed = session?.status === "CLOSED";
  // Prefer the server counter list; fall back to codes if it hasn't loaded.
  const counterOptions = counters.length
    ? counters
    : COUNTER_CODES.map((code) => ({ code, name: code }));

  // Empty when opening is allowed; otherwise the tooltip explaining why not.
  const openBlockedReason = !counterCode
    ? "Select a counter first"
    : lockedByOther
      ? `Already open${lockedByLabel ? ` by ${lockedByLabel}` : " by another user"} — pick a free counter`
      : "";

  const doOpen = async (payload) => {
    const r = await cc.openSession(payload);
    if (r.ok) setModal(null);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <ErrorBar message={error} onRetry={canRetry ? retry : null} busy={busy} />

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <IpdSectionCard>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <Typography component="span" sx={{ ...ipdLabelSx, display: "inline", mb: 0 }}>Counter</Typography>
            <IpdTextField
              select
              value={counterCode}
              onChange={(e) => setCounterCode(e.target.value)}
              disabled={isOpen || busy}
              SelectProps={{ displayEmpty: true }}
              sx={{ width: 180, "& .MuiSelect-select": { fontWeight: 700 } }}
            >
              {!counterCode && <MenuItem value="">Select counter…</MenuItem>}
              {counterOptions.map((c) => {
                // A counter another cashier holds open stays visible but unusable —
                // showing who has it is more useful than hiding the row.
                const taken = Boolean(c.isOpen && !c.openedByMe);
                return (
                  <MenuItem key={c.code} value={c.code} disabled={taken}>
                    {c.name}
                    {taken && (
                      <Box component="span" sx={{ marginLeft: "8px", fontSize: "11px", fontWeight: 600, color: C.textSub }}>
                        · in use{c.openedBy ? ` by ${c.openedBy}` : ""}
                      </Box>
                    )}
                  </MenuItem>
                );
              })}
            </IpdTextField>
            <SessionChip status={session?.status} />
            {lockedByOther && (
              <Box component="span" sx={{ fontSize: "11px", fontWeight: 700, color: IPD_COLORS.warning.text, backgroundColor: IPD_COLORS.warning.bg, border: `1px solid ${IPD_COLORS.warning.border}`, borderRadius: "100px", padding: "4px 12px" }}>
                In use{lockedByLabel ? ` by ${lockedByLabel}` : " by another user"}
              </Box>
            )}
            {session?.openedAt && (
              <Box component="span" sx={{ fontSize: "11px", color: C.textSub, fontWeight: 600 }}>Opened {fmtDateTime(session.openedAt)}</Box>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {!isOpen && canInterimView && (
              <Tooltip title={openBlockedReason} disableHoverListener={!openBlockedReason}>
                <Box component="span">
                  <IpdButton
                    tone="teal"
                    startIcon={<SvgIcon name="cash" size={15} color={IPD_COLORS.white} />}
                    onClick={() => setModal("open")}
                    disabled={busy || Boolean(openBlockedReason)}
                  >
                    {isClosed ? "Open New Session" : "Open Session"}
                  </IpdButton>
                </Box>
              </Tooltip>
            )}
            {isOpen && (
              <IpdButton
                variant="outlined"
                startIcon={<SvgIcon name="refresh" size={15} color={C.text} />}
                onClick={cc.refreshInterim}
                disabled={busy}
              >
                Refresh
              </IpdButton>
            )}
          </Box>
        </Box>
      </IpdSectionCard>

      {/* ── Summary cards — hidden once the session is closed ──────────────── */}
      {!isClosed && (
      <IpdSectionCard title="Today's summary">
        {!scroll ? (
          <Typography sx={{ margin: 0, fontSize: "13px", color: C.textSub }}>
            {session ? "Tap Refresh to load live totals for this session." : "Open a session to start collecting and viewing totals."}
          </Typography>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
              <StatTile label="Receipts today" value={fmtINR(scroll.receiptAmount)} sub={`${scroll.receiptCount} receipt${scroll.receiptCount === 1 ? "" : "s"}`} tone={C.success} />
              <StatTile label="Refunds today" value={fmtINR(scroll.refundAmount)} sub={`${scroll.refundCount} refund${scroll.refundCount === 1 ? "" : "s"}`} tone={C.danger} />
              <StatTile label="Net collection" value={fmtINR(scroll.netCollection)} sub="Receipts − refunds" />
              <StatTile label="Deposits" value={fmtINR(scroll.depositAmount)} sub={`${scroll.depositCount} entr${scroll.depositCount === 1 ? "y" : "ies"}`} />
            </Box>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>Mode split</Typography>
            <ModeTotals modes={scroll.modes} fmt={fmtINR} />
          </>
        )}
      </IpdSectionCard>
      )}

      {/* ── Actions — only while the session is open ───────────────────────── */}
      {isOpen && (
        <IpdSectionCard title="Actions">
          <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {canInterimView && (
              <IpdButton
                variant="outlined"
                startIcon={<SvgIcon name="eye" size={15} color={C.text} />}
                onClick={onViewInterim}
                disabled={busy}
              >
                View Interim Scroll
              </IpdButton>
            )}
            {canInterimPrint && (
              <IpdButton
                variant="outlined"
                startIcon={<SvgIcon name="printer" size={15} color={C.text} />}
                onClick={cc.printInterim}
                disabled={busy || !isOpen}
              >
                Print Interim
              </IpdButton>
            )}
            {(canFinalView || canFinalClose) && (
              <IpdButton
                tone="teal"
                startIcon={<SvgIcon name="lock" size={15} color={IPD_COLORS.white} />}
                onClick={onReviewFinal}
                disabled={busy || !isOpen}
              >
                {finalScrollId ? "Finalize & Close" : "Final Scroll & Close"}
              </IpdButton>
            )}
          </Box>
          <Typography sx={{ margin: "10px 0 0", fontSize: "11px", color: C.textSub }}>
            Reconcile declared cash, generate the final scroll, then close the day.
          </Typography>
        </IpdSectionCard>
      )}

      {modal === "open" && (
        <OpenSessionModal counterCode={counterCode} busy={busy} onClose={() => setModal(null)} onSubmit={doOpen} />
      )}
    </Box>
  );
}
